import React from "react";
import Chart from "react-apexcharts";
import dayjs from "dayjs";
import PropellerLayoutCurve from "../AdditionalCharts/PropellantChart";

const groupDataByDateAndUnit = (data) => {
  const grouped = {};
  
  data.forEach(({ date, unit, field }) => {
    const dateKey = dayjs(date).format("YYYY-MM-DD"); // ✅ Full date grouping
    if (!grouped[dateKey]) grouped[dateKey] = {};
    if (!grouped[dateKey][unit]) grouped[dateKey][unit] = [];
    grouped[dateKey][unit].push(field);
  });
  
  return grouped;
};

const getSeriesByMethod = (groupedData, method) => {
  const categories = Object.keys(groupedData).sort(); // ✅ Sorted dates for x-axis
  const unitsSet = new Set();
  
  categories.forEach((date) => {
    Object.keys(groupedData[date]).forEach((u) => unitsSet.add(Number(u)));
  });
  
  const units = Array.from(unitsSet).sort((a, b) => a - b);
  
  const series = units.map((unit) => {
    const data = categories.map((date) => {
      const fields = groupedData[date]?.[unit] || [];
      if (fields.length === 0) return null;
      const avg = fields.reduce((a, b) => a + b, 0) / fields.length;
      
      switch (method) {
        case "AVG":
          const allFields = Object.values(groupedData[date]).flat();
          return (avg / allFields.length).toFixed(2);
        case "DEVIATION": {
          const allFields = Object.values(groupedData[date]).flat();
          const dayAvg =
            allFields.reduce((a, b) => a + b, 0) / allFields.length;
          return Number((avg - dayAvg).toFixed(2));
        }
        default:
          return fields[0];
      }
    });
    
    return {
      name: `Unit ${unit}`,
      data,
    };
  });
  
  return { series, categories };
};

// Generate different shades of the base color for each unit
const generateColorVariants = (baseColor, count) => {
  // If baseColor is a hex color, generate variants
  if (baseColor.startsWith('#')) {
    const colors = [];
    for (let i = 0; i < count; i++) {
      // Create darker/lighter variants
      const factor = 0.8 + (i * 0.4) / (count - 1); // Range from 0.8 to 1.2
      const adjustedColor = adjustBrightness(baseColor, factor);
      colors.push(adjustedColor);
    }
    return colors;
  }
  
  // Default color palette if base color is not hex
  return [
    '#A52A2A', '#B22222', '#CD5C5C', '#DC143C', 
    '#FF6347', '#FF4500', '#FF0000'
  ].slice(0, count);
};

// Helper function to adjust brightness of hex color
const adjustBrightness = (hex, factor) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  
  const newR = Math.min(255, Math.floor(r * factor));
  const newG = Math.min(255, Math.floor(g * factor));
  const newB = Math.min(255, Math.floor(b * factor));
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

const MonthlyBarChart = ({ data, chartaxis }) => {
  return (
    <div>
      {data.map((dataset, index) => {
        const grouped = groupDataByDateAndUnit(dataset.data);
        const { series, categories } = getSeriesByMethod(
          grouped,
          dataset.chart_method
        );
        
        // Generate color variants for each unit
        const colors = generateColorVariants(dataset.color, series.length);
        
        const options = {
          chart: {
            type: "bar",
            stacked: false,
            toolbar: { show: false },
          },
          xaxis: {
            categories,
            title: { text: chartaxis.axis?.x },
          },
          yaxis: {
            title: { text: chartaxis.axis?.y },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: "60%", // Adjust width as needed
              borderRadius: 4,
              dataLabels: {
                position: 'top'
              }
            },
          },
          colors: colors, // Use different colors for each unit
          title: {
            text: dataset.name,
            align: "left",
          },
          legend: {
            show: true,
            position: 'top'
          },
          dataLabels: {
            enabled: true,
            offsetY: -15,  
            style: {
              colors: ['#000'],  
              fontSize: '12px',
              fontWeight: 'bold'
            }
          },
          // Add spacing between bars
          stroke: {
            show: true,
            width: 5,
            colors: ['#fff']
          }
        };
       
        return (
          <>
           <div key={index} style={{ marginBottom: "30px" }}>
            <Chart
              options={options}
              series={series}
              type="bar"
              height={400}
            />
          </div>
          </>
        );
      })}
    </div>
  );
};

export default MonthlyBarChart;